import { useState, useCallback, useRef } from "react";
import { integrationsService } from "../services/integrationsService";
import { useIntegrationModals } from "./useIntegrationModals";

interface UseIntegrationConnectionOptions {
  onConnectionSuccess?: (provider: string) => void;
  onConnectionError?: (provider: string, error: Error) => void;
  onConnectionClose?: (provider: string) => void;
}

/**
 * Reusable hook for connecting integrations via Nango OAuth flow.
 * Extracted from Integrations.tsx for use in inline connection scenarios.
 *
 * Usage:
 *   const { connect, isConnecting, connectingProvider, renderModals } = useIntegrationConnection({
 *     onConnectionSuccess: (provider) => refreshIntegrations(),
 *   });
 *
 *   // In JSX:
 *   <button onClick={() => connect('github')} disabled={isConnecting}>
 *     Connect GitHub
 *   </button>
 *   {renderModals()}
 */
export function useIntegrationConnection(options?: UseIntegrationConnectionOptions) {
  const [connectingProvider, setConnectingProvider] = useState<string | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // Use ref to avoid stale closure issues
  const doConnectRef = useRef<((provider: string) => void) | null>(null);

  // Reuse the shared pre-connection modal logic
  const { preConnectCheck, renderModals, activeModal } = useIntegrationModals(
    (provider: string) => doConnectRef.current?.(provider)
  );

  /**
   * Verify connection was successfully stored by polling the backend.
   * Uses exponential backoff to give Nango webhook time to process.
   */
  const verifyConnection = useCallback(async (provider: string): Promise<boolean> => {
    // Step 1: Targeted sync (should work immediately after Nango connect)
    try {
      const integration = await integrationsService.syncProviderConnection(provider);
      if (integration && integration.integration_status === "active") {
        console.log(
          `[useIntegrationConnection] Connection verified for ${provider} via sync endpoint`
        );
        return true;
      }
    } catch (e) {
      console.warn(
        "[useIntegrationConnection] Sync check failed, falling back to polling:",
        e
      );
    }

    // Step 2: Short fallback polling (3 retries, 500ms)
    for (let attempt = 0; attempt < 3; attempt++) {
      await new Promise((r) => setTimeout(r, 500));
      try {
        const integrations = await integrationsService.getUserIntegrations();
        const isConnected = integrations.some(
          (conn) =>
            conn.provider === provider && conn.integration_status === "active"
        );
        if (isConnected) {
          console.log(
            `[useIntegrationConnection] Connection verified for ${provider} after fallback attempt ${attempt + 1}`
          );
          return true;
        }
      } catch (error) {
        console.error(
          `[useIntegrationConnection] Fallback retry ${attempt + 1} failed:`,
          error
        );
      }
    }
    return false;
  }, []);

  /**
   * Core connection logic - initiates OAuth flow with callbacks
   */
  const doConnect = useCallback(
    async (provider: string) => {
      try {
        setConnectingProvider(provider);
        setConnectionError(null);

        console.log(`[useIntegrationConnection] Starting connection for ${provider}`);

        await integrationsService.connectIntegration(provider, {
          onSuccess: async () => {
            console.log(`[useIntegrationConnection] OAuth success for ${provider}`);

            // Verify connection with retry
            const connected = await verifyConnection(provider);
            setConnectingProvider(null);

            if (connected) {
              console.log(`[useIntegrationConnection] Connection verified, calling onConnectionSuccess`);
              options?.onConnectionSuccess?.(provider);
            } else {
              console.warn(
                `[useIntegrationConnection] Connection for ${provider} not found after retries`
              );
              setConnectionError("Connection not verified. Please try again.");
              options?.onConnectionError?.(
                provider,
                new Error("Connection not verified after OAuth success")
              );
            }
          },
          onError: (error: Error) => {
            console.error(`[useIntegrationConnection] OAuth error for ${provider}:`, error);
            setConnectionError(error.message);
            setConnectingProvider(null);
            options?.onConnectionError?.(provider, error);
          },
          onClose: () => {
            console.log(`[useIntegrationConnection] OAuth closed for ${provider}`);
            setConnectingProvider(null);
            options?.onConnectionClose?.(provider);
          },
        });
      } catch (error) {
        console.error(`[useIntegrationConnection] Failed to connect ${provider}:`, error);
        const err = error instanceof Error ? error : new Error(String(error));
        setConnectionError(err.message);
        setConnectingProvider(null);
        options?.onConnectionError?.(provider, err);
      }
    },
    [verifyConnection, options]
  );

  // Keep ref updated
  doConnectRef.current = doConnect;

  /**
   * Public method to initiate connection.
   * Runs pre-connection checks (modals) before starting OAuth.
   */
  const connect = useCallback(
    (provider: string) => {
      // preConnectCheck returns false if a modal was shown (caller should wait)
      if (!preConnectCheck(provider)) {
        return;
      }
      doConnect(provider);
    },
    [preConnectCheck, doConnect]
  );

  /**
   * Clear any connection error state
   */
  const clearError = useCallback(() => {
    setConnectionError(null);
  }, []);

  return {
    /** Initiate connection for a provider */
    connect,
    /** Currently connecting provider (null if not connecting) */
    connectingProvider,
    /** Whether any connection is in progress */
    isConnecting: !!connectingProvider,
    /** Last connection error message */
    connectionError,
    /** Clear the error state */
    clearError,
    /** Whether a pre-connection modal is showing */
    isModalOpen: !!activeModal,
    /** Render pre-connection modals - include in JSX */
    renderModals,
  };
}
